import { call, put, select } from 'redux-saga/effects';

import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { isUnpublishedWorkbookDraft } from '~src/drafts/utils/DraftUtils';
import { invalidateProjectQueries, projectQueryKeys } from '~src/projects/services/Queries';
import { RenameAction } from '~src/spacesPlatform/actions/RenameAction';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { RenameContentAction } from '../../actions/RenameContentAction';
import { requestSpaceContents } from '../../actions/RequestSpaceContentsActions';
import { IContentInfo } from '../../models/IContentInfo';
import { replaceDatasourceIdWithConnectionIdForMixedContent } from '../SpacesSagaHelpers';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';

export function* handleRenameContent(action: RenameContentAction) {
  try {
    let isOwner: boolean = false;
    if (isUnpublishedWorkbookDraft(action.content)) {
      isOwner = true;
    } else {
      const ownerSelector = yield call(CompositeSessionInfoSelectors.currentUserIsOwnerSelector, action.content.owner);
      isOwner = yield select(ownerSelector);
    }

    //Need to replace datasource ID in the content to mitigate Datasource API expecting DataConnectionId
    let mutableContentArrayCopy: IContentInfo[] = [];
    mutableContentArrayCopy = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, [action.content]);

    const result = yield call(openRenameDialog, mutableContentArrayCopy[0], isOwner);

    if (result && result.errors && result.errors.length > 0) {
      const localizedErrorMessage = yield call(getLocalizedErrorMessage, action.content);
      yield put(requestFailed(result.errors[0].message, localizedErrorMessage));
    } else {
      yield call(invalidateQueries, action.content);

      const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.content]);
      if (!maybeReloaded) {
        yield put(requestSpaceContents());
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getLocalizedErrorMessage(content: IContentInfo) {
  if (content.contentType === 'project') {
    return Messages.projectRenameError({ PROJECT_NAME: content.name });
  } else if (content.contentType === 'workbook') {
    return Messages.workbookRenameError({ WORKBOOK_NAME: content.name });
  } else if (content.contentType === 'flow') {
    return Messages.flowRenameError({ FLOW_NAME: content.name });
  } else if (content.contentType === 'schedule') {
    return Messages.scheduleRenameError({ SCHEDULE_NAME: L10n.elideIfLong(content.name) });
  } else if (content.contentType === 'group') {
    return Messages.groupRenameError({ GROUP_NAME: content.name });
  } else if (content.contentType === 'virtualConnection') {
    return Messages.publishedConnectionRenameError({ PUBLISHED_CONNECTION_NAME: content.name });
  } else if (content.contentType === 'collection') {
    return Messages.collectionRenameError({ COLLECTION_NAME: content.name });
  } else if (content.contentType === 'datasource') {
    return Messages.datasourceRenameError({DATASOURCE_NAME: content.name});
  }
  return Messages.errorUnknown();
}

export function openRenameDialog(content: IContentInfo, isOwner: boolean): Promise<IResult> {
  if (content.contentType === 'project') {
    return RenameAction.renameProject(content);
  } else if (content.contentType === 'workbook') {
    return RenameAction.renameWorkbook(content as IContentInfo<'workbook'>);
  } else if (content.contentType === 'flow') {
    return RenameAction.renameFlow(content as IContentInfo<'flow'>);
  } else if (content.contentType === 'schedule') {
    return RenameAction.renameSchedule(content);
  } else if (content.contentType === 'group') {
    return RenameAction.renameGroup(content);
  } else if (content.contentType === 'virtualConnection') {
    return RenameAction.renamePublishedConnection(content);
  } else if (content.contentType === 'collection') {
    return RenameAction.renameCollection(content, isOwner);
  } else if (content.contentType === 'datasource') {
    return RenameAction.renameDatasource(content);
  }
}

export function invalidateQueries(content: IContentInfo) {
  if (content.contentType === 'project') {
    invalidateProjectQueries(projectQueryKeys.all());
  }
}